// Analytics (SEO P8): GA4 via VITE_GA_MEASUREMENT_ID, production builds only.
// Loads gtag deferred, tracks SPA route changes, and exposes conversion events.
// If the env var is unset this module is a complete no-op.
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
const IS_PROD = import.meta.env.PROD
const PRIVACY_CHOICE_KEY = 'debeach-privacy-choice'
const PRIVACY_CHOICE_EVENT = 'debeach:privacy-choice'

const hasAnalyticsConsent = () => {
  try {
    return localStorage.getItem(PRIVACY_CHOICE_KEY) === 'accepted'
  } catch {
    return false
  }
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let initialized = false

function init() {
  if (!IS_PROD || !GA_ID || initialized || !hasAnalyticsConsent()) return
  initialized = true
  const s = document.createElement('script')
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  s.async = true
  document.head.appendChild(s)
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args)
  }
  window.gtag('js', new Date())
  window.gtag('config', GA_ID, { send_page_view: false })

  // phone_click: delegated listener covers every tel: link site-wide.
  document.addEventListener('click', (e) => {
    const anchor = (e.target as HTMLElement).closest('a[href^="tel:"]')
    if (anchor) {
      trackEvent('phone_click', {
        number: anchor.getAttribute('href')!.replace('tel:', ''),
      })
    }
  })
}

/** Fire a conversion/engagement event (no-op without GA4 configured). */
export function trackEvent(
  name: 'contact_submit' | 'phone_click' | 'listing_view' | string,
  params: Record<string, string | number> = {},
) {
  if (!initialized || !window.gtag) return
  window.gtag('event', name, params)
}

/** SPA pageview tracking — mount once in App. */
export function usePageTracking() {
  const location = useLocation()
  useEffect(() => {
    const trackPage = () => {
      init()
      if (!initialized || !window.gtag || !GA_ID) return
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      })
    }
    trackPage()
    window.addEventListener(PRIVACY_CHOICE_EVENT, trackPage)
    return () => window.removeEventListener(PRIVACY_CHOICE_EVENT, trackPage)
  }, [location])
}
